<template>
  <div class="gantt-card">
    <div class="gantt-head">
      <h3 class="gantt-title">
        <PortalIcon name="Flag" :size="15" style="color: #003ca5" />
        Cronograma de proyectos
      </h3>
      <div class="search">
        <PortalIcon name="Search" :size="15" style="color: var(--pt-muted)" />
        <input
          v-model="q"
          placeholder="Buscar cliente, asunto o folio…"
          class="search-inp"
        />
      </div>
      <select v-model="fCliente" class="select" title="Filtrar por cliente">
        <option v-for="c in clientes" :key="c">{{ c }}</option>
      </select>
      <select v-model="fArea" class="select">
        <option v-for="a in areas" :key="a">{{ a }}</option>
      </select>
      <select v-model="fEstatus" class="select">
        <option v-for="e in ['Todos', ...ESTATUS]" :key="e">{{ e }}</option>
      </select>
      <select v-model="fPrioridad" class="select" title="Filtrar por prioridad">
        <option v-for="p in prioridades" :key="p">{{ p }}</option>
      </select>
      <select v-model="ver" class="select">
        <option v-for="v in ['Abiertos', 'Todos', 'Proyectos']" :key="v">{{ v }}</option>
      </select>
      <select v-model="escala" class="select" title="Escala del cronograma">
        <option v-for="e in escalas" :key="e.id" :value="e.id">{{ e.label }}</option>
      </select>
    </div>

    <div v-if="!datos.length" class="empty">No hay proyectos para mostrar.</div>

    <div v-else ref="scroll" class="gantt-scroll">
      <div class="gantt-inner" :style="{ width: labelW + timelineW + 'px' }">
        <!-- Columna de etiquetas (fija) -->
        <div class="label-col" :style="{ width: labelW + 'px' }">
          <div class="label-head" :style="{ height: headerH + 'px' }">Proyecto</div>
          <div
            v-for="r in datos"
            :key="r.id"
            class="label-row"
            :style="{ height: rowH + 'px' }"
          >
            <span class="ldot" :style="{ background: colorArea(r.area) }"></span>
            <div class="ltxt">
              <div class="lcli">{{ r.cliente }}</div>
              <div class="lasu">{{ r.asunto }}</div>
            </div>
          </div>
        </div>

        <!-- Línea de tiempo -->
        <div class="timeline" :style="{ width: timelineW + 'px' }">
          <div class="tl-head" :style="{ height: headerH + 'px' }">
            <div
              v-for="(t, i) in ticks"
              :key="i"
              class="tick-label"
              :style="{ left: t.left + 'px' }"
            >
              {{ t.label }}
            </div>
          </div>
          <div class="tl-body" :style="{ height: datos.length * rowH + 'px' }">
            <div
              v-for="(t, i) in ticks"
              :key="'g' + i"
              class="grid-line"
              :style="{ left: t.left + 'px' }"
            ></div>
            <div v-if="hoyX !== null" class="hoy-line" :style="{ left: hoyX + 'px' }">
              <span class="hoy-tag">Hoy</span>
            </div>
            <div
              v-for="b in bars"
              :key="b.r.id"
              class="bar"
              :title="b.title"
              :style="{
                left: b.left + 'px',
                top: b.top + 'px',
                width: b.w + 'px',
                background: softArea(b.r.area),
                border: '1px solid ' + (b.overdue ? '#dc2626' : b.col),
              }"
            >
              <div
                class="bar-prog"
                :style="{ width: b.av + '%', background: b.col }"
              ></div>
              <span
                v-if="b.showAv"
                class="bar-av"
                :style="{ color: b.avTextColor }"
              >{{ b.av }}%</span>
              <span v-if="b.est === 'Concluido'" class="bar-ic">
                <PortalIcon
                  name="CheckCircle2"
                  :size="13"
                  :style="{ color: b.r._sla === 'Tarde' ? '#dc2626' : '#16a34a' }"
                />
              </span>
              <span v-else-if="b.est === 'Stand by'" class="bar-ic">
                <PortalIcon name="PauseCircle" :size="13" style="color: #d97706" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="gantt-legend">
      <span class="leg"><span class="leg-hoy"></span>Hoy</span>
      <span class="leg"><span class="leg-venc"></span>Vencido</span>
      <span class="leg">
        <PortalIcon name="CheckCircle2" :size="12" style="color: #16a34a" />Concluido
      </span>
      <span class="leg">
        <PortalIcon name="PauseCircle" :size="12" style="color: #d97706" />Stand by
      </span>
      <span class="leg-note">· El relleno indica el % de avance</span>
    </div>
  </div>
</template>

<script>
import PortalIcon from "./PortalIcon.vue";
import { DIA, ESTATUS } from "./portal.constants";
import {
  normEstatus,
  colorArea,
  softArea,
  rangoDe,
  fmt,
} from "./portal.helpers";

export default {
  name: "CronogramaTab",
  components: { PortalIcon },
  props: {
    reg: { type: Array, default: () => [] },
    now: { type: Date, required: true },
  },
  data() {
    return {
      ESTATUS,
      fCliente: "Todos",
      fArea: "Todas",
      fEstatus: "Todos",
      fPrioridad: "Todas",
      q: "",
      ver: "Abiertos",
      // Escala del scroll horizontal: controla el ancho por día (px/día) y el
      // paso de los ticks superiores. De "Semana" (detalle diario) a "Año"
      // (vista comprimida con marcas mensuales).
      escala: "Mes",
      escalas: [
        { id: "Semana", label: "Semana", pxDay: 48, step: 1 },
        { id: "Quincena", label: "Quincena", pxDay: 30, step: 2 },
        { id: "Mes", label: "Mes", pxDay: 22, step: 7 },
        { id: "Trimestre", label: "Trimestre", pxDay: 10, step: 15 },
        { id: "Año", label: "Año", pxDay: 3.6, step: 30 },
      ],
      labelW: 230,
      headerH: 40,
      rowH: 40,
    };
  },
  computed: {
    clientes() {
      return [
        "Todos",
        ...Array.from(
          new Set(this.reg.map((r) => r.cliente).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b, "es")),
      ];
    },
    areas() {
      return ["Todas", ...Array.from(new Set(this.reg.map((r) => r.area)))];
    },
    prioridades() {
      return [
        "Todas",
        ...Array.from(new Set(this.reg.map((r) => r.prioridad).filter(Boolean))),
      ];
    },
    datos() {
      let d = this.reg.filter((r) => {
        if (this.fCliente !== "Todos" && r.cliente !== this.fCliente) return false;
        if (this.fArea !== "Todas" && r.area !== this.fArea) return false;
        if (this.fEstatus !== "Todos" && normEstatus(r.estatus) !== this.fEstatus)
          return false;
        if (this.fPrioridad !== "Todas" && r.prioridad !== this.fPrioridad)
          return false;
        if (
          this.q &&
          !`${r.folio} ${r.cliente} ${r.asunto}`
            .toLowerCase()
            .includes(this.q.toLowerCase())
        )
          return false;
        return true;
      });
      if (this.ver === "Abiertos")
        d = d.filter(
          (r) =>
            normEstatus(r.estatus) !== "Concluido" &&
            normEstatus(r.estatus) !== "Declinado"
        );
      if (this.ver === "Proyectos")
        d = d.filter((r) => r.categoria === "Proyecto");
      return d
        .slice()
        .sort((a, b) => rangoDe(a).start - rangoDe(b).start);
    },
    t0() {
      return (
        Math.min(
          ...this.datos.map((r) => rangoDe(r).start.getTime()),
          this.now.getTime()
        ) -
        2 * DIA
      );
    },
    t1() {
      return (
        Math.max(
          ...this.datos.map((r) => rangoDe(r).end.getTime()),
          this.now.getTime()
        ) +
        3 * DIA
      );
    },
    totalMs() {
      return this.t1 - this.t0 || DIA;
    },
    totalDays() {
      return this.totalMs / DIA;
    },
    // Configuración de la escala activa (px/día y paso de ticks).
    escalaCfg() {
      return this.escalas.find((e) => e.id === this.escala) || this.escalas[2];
    },
    timelineW() {
      return Math.max(640, Math.round(this.totalDays * this.escalaCfg.pxDay));
    },
    stepDays() {
      return this.escalaCfg.step;
    },
    ticks() {
      const out = [];
      // Con pasos largos (mensual) la etiqueta muestra el mes; con pasos
      // cortos, el día. Así los indicadores superiores se adaptan a la escala.
      const mensual = this.stepDays >= 28;
      for (let t = this.t0; t <= this.t1; t += this.stepDays * DIA) {
        const iso = new Date(t).toISOString().slice(0, 10);
        out.push({
          left: this.posX(t),
          label: mensual ? this.fmtMes(iso) : fmt(iso),
        });
      }
      return out;
    },
    hoyX() {
      const n = this.now.getTime();
      return n >= this.t0 && n <= this.t1 ? this.posX(n) : null;
    },
    bars() {
      return this.datos.map((r, idx) => {
        const { start, end } = rangoDe(r);
        const left = this.posX(start.getTime());
        const w = Math.max(8, this.posX(end.getTime()) - left);
        const est = normEstatus(r.estatus);
        const av = r._av != null ? r._av : 0;
        const col = colorArea(r.area);
        const title =
          `${r.folio} · ${r.cliente}\n${r.asunto}\n` +
          `${r.area} · ${est} · ${r.prioridad}\n` +
          `Inicio ${fmt(r.fechaInicial)}` +
          `${r.fechaCompromiso ? " · Compromiso " + fmt(r.fechaCompromiso) : ""}` +
          `${r.fechaFinal ? " · Cierre " + fmt(r.fechaFinal) : ""}\n` +
          `Avance ${av}%`;
        return {
          r,
          left,
          top: idx * this.rowH + 9,
          w,
          av,
          col,
          est,
          overdue: r._sla === "Vencido",
          showAv: w > 42,
          avTextColor: av > 55 ? "#fff" : col,
          title,
        };
      });
    },
  },
  mounted() {
    // Al abrir, posiciona el scroll al final para mostrar de una vez lo más
    // reciente (incluido "Hoy", que queda cerca del extremo derecho).
    this.$nextTick(this.scrollAlFinal);
  },
  watch: {
    // Reancla al extremo derecho cuando cambia el ancho de la línea de tiempo
    // (cambio de escala o de datos filtrados).
    timelineW() {
      this.$nextTick(this.scrollAlFinal);
    },
  },
  methods: {
    colorArea,
    softArea,
    scrollAlFinal() {
      const el = this.$refs.scroll;
      if (el) el.scrollLeft = el.scrollWidth;
    },
    posX(ms) {
      return ((ms - this.t0) / this.totalMs) * this.timelineW;
    },
    // Etiqueta mensual "jun 26" para las escalas amplias (Año).
    fmtMes(iso) {
      return new Date(iso + "T00:00:00").toLocaleDateString("es-MX", {
        month: "short",
        year: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.gantt-card {
  background: var(--pt-card);
  border: 1px solid var(--pt-border);
  border-radius: 16px;
  overflow: hidden;
}
.gantt-head {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 16px 16px 12px;
}
.gantt-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: var(--pt-text);
  margin: 0 auto 0 0;
}
.search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  flex: 1;
  min-width: 180px;
  background: var(--pt-card);
  border: 1px solid var(--pt-border);
}
.search-inp {
  background: transparent;
  border: 0;
  outline: none;
  color: var(--pt-text);
  font-size: 14px;
  width: 100%;
  font-family: inherit;
}
.select {
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  background: var(--pt-card);
  color: var(--pt-text);
  border: 1px solid var(--pt-border);
  outline: none;
  font-family: inherit;
}
.empty {
  text-align: center;
  font-size: 14px;
  color: var(--pt-muted);
  padding: 32px 0;
  border-top: 1px solid var(--pt-border);
}
.gantt-scroll {
  overflow-x: auto;
  border-top: 1px solid var(--pt-border);
}
.gantt-inner {
  display: flex;
}
.label-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background: var(--pt-card);
  border-right: 1px solid var(--pt-border);
  flex-shrink: 0;
}
.label-head {
  display: flex;
  align-items: center;
  padding: 0 16px;
  font-size: 12px;
  font-weight: 600;
  color: var(--pt-muted);
  text-transform: uppercase;
}
.label-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  border-top: 1px solid var(--pt-line);
}
.ldot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex-shrink: 0;
}
.ltxt {
  min-width: 0;
}
.lcli {
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lasu {
  font-size: 11px;
  color: var(--pt-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.timeline {
  position: relative;
  flex-shrink: 0;
}
.tl-head {
  position: relative;
  border-bottom: 1px solid var(--pt-border);
}
.tick-label {
  position: absolute;
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
  font-size: 10px;
  color: var(--pt-muted);
  padding-left: 4px;
}
.tl-body {
  position: relative;
}
.grid-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: var(--pt-track);
}
.hoy-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #003ca5;
  z-index: 1;
}
.hoy-tag {
  position: absolute;
  top: 2px;
  left: 3px;
  font-size: 10px;
  font-weight: 600;
  color: #fff;
  background: #003ca5;
  border-radius: 3px;
  padding: 1px 4px;
}
.bar {
  position: absolute;
  height: 22px;
  border-radius: 7px;
  overflow: hidden;
  z-index: 1;
}
.bar-prog {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  opacity: 0.85;
}
.bar-av {
  position: absolute;
  left: 6px;
  top: 3px;
  font-size: 11px;
  font-weight: 600;
  z-index: 1;
}
.bar-ic {
  position: absolute;
  right: 4px;
  top: 4px;
}
.gantt-legend {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding: 12px 16px;
  font-size: 12px;
  color: var(--pt-dim);
  border-top: 1px solid var(--pt-border);
}
.leg {
  display: flex;
  align-items: center;
  gap: 6px;
}
.leg-hoy {
  width: 12px;
  height: 2px;
  background: #003ca5;
}
.leg-venc {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  border: 1px solid #dc2626;
}
.leg-note {
  color: var(--pt-muted);
}
</style>
